import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import Cookies from 'js-cookie'
import { Dispatch } from 'redux'
import {
  getSubTrades,
  addSubTrade,
  editSubTrade,
  getSubTrade,
  deleteSubTrade,
  startLoadingSubTrades,
  stopLoadingSubTrades,
  editSubTradeFee,
  addSubTradeFee,
  editLockedSubTradeFee,
  addLockedSubTradeFee,
  deleteSubTradeFee,
  changeAllocationSubTrade,
  changeWireInstructionSubTrade,
  settleSubTradeAction,
  unsettleSubTradeAction,
  terminateSubTradeAction,
} from 'app/state/ducks/sub-trades/actions'

import { SubTradeAllocation, SubTradeParams } from 'app/models/sub-trade-params'
import {
  coalesceThunk,
  coalesceThunkByKey,
} from 'app/state/utils/coalesce-thunk'
import { removeSignableDocumentsForContract } from 'app/state/ducks/signable-document/actions'
import {
  loadAllLockedFMMetadatas,
  loadFMMetadataForSubTrade,
} from 'app/state/ducks/signable-document/thunks'

export const saveSubTrade =
  (savedSubTrade: SubTradeParams, subTrade?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    if (subTrade) {
      return client
        .put(`${apiServerUrl}/api/sub-trades`, savedSubTrade)
        .then(subTrade => {
          dispatch(editSubTrade(subTrade.data))
          return { success: true, payload: subTrade.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    } else {
      return client
        .post(`${apiServerUrl}/api/sub-trades`, savedSubTrade)
        .then(subTrade => {
          dispatch(addSubTrade(subTrade.data))
          return { success: true, payload: subTrade.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }
  }

const loadOpenSubTrades = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/sub-trades`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

const loadSettledSubTrades = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/locked-sub-trades`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const loadAllSubTrades = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingSubTrades())
      const [openSubTrade, settledSubTrades] = await Promise.all([
        loadOpenSubTrades(),
        loadSettledSubTrades(),
      ])

      const subTrades = settledSubTrades.map(
        (a: { subTrade: any; effectiveDate: string; status: string; terminationComment?: string | null }) => ({
          ...a.subTrade,
          status: a.status,
          lockedDate: a.effectiveDate,
          terminationComment: a.terminationComment,
        }),
      )

      const allSubTrades = openSubTrade
        .map((subTrade: SubTradeParams) => ({ ...subTrade, status: 'Open' }))
        .concat(subTrades)
      return dispatch(getSubTrades(allSubTrades))
    } catch (error) {
      console.log('THUNK ERROR: ', error)
    } finally {
      dispatch(stopLoadingSubTrades())
    }
  },
)

export const loadOneSubTrade = coalesceThunkByKey(
  (subTradeId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const isTerminated = status.toLowerCase() === 'terminated'
    const subTradePath = isTerminated ? 'locked-sub-trades' : 'sub-trades'

    return client
      .get(`${apiServerUrl}/api/${subTradePath}/one?id=${subTradeId}`)
      .then(subTrades => {
        const subTradeData =
          subTradePath === 'locked-sub-trades'
            ? {
                ...subTrades.data.subTrade,
                status: subTrades.data.status,
                lockedDate: subTrades.data.effectiveDate,
                terminationComment: subTrades.data.terminationComment,
              }
            : { ...subTrades.data.subTrade, status: 'Open' }
        return dispatch(getSubTrade(subTradeData))
      })
      .catch(e => {
        return {
          success: false,
          payload: e.response?.data?.error ?? 'Unknown error',
        }
      })
  },
)

export const setOneSubTrade = (subTrade: any) => async (dispatch: Dispatch) => {
  dispatch(getSubTrade({ ...subTrade }))
}

export const deleteSubTrades =
  (ids: string[]) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(`${apiServerUrl}/api/sub-trades?ids=${ids}`)
      .then(response => {
        return dispatch(deleteSubTrade(response.data))
      })
      .catch(e => {
        console.log('THUNK ERROR', e)
      })
  }

export const saveSubTradeFee =
  (subTrade: SubTradeParams, savedSubTradeFee: any, subTradeFee?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    const isLocked = subTrade?.status?.toLowerCase() === 'settled'

    const subTradeApiPath = isLocked
      ? `${apiServerUrl}/api/locked-sub-trades`
      : `${apiServerUrl}/api/sub-trades`

    const dispatchEdit = isLocked ? editLockedSubTradeFee : editSubTradeFee
    const dispatchAdd = isLocked ? addLockedSubTradeFee : addSubTradeFee

    if (subTradeFee) {
      return client
        .put(`${subTradeApiPath}/edit-fee`, savedSubTradeFee)
        .then(subTradeFee => {
          dispatch(dispatchEdit(subTradeFee.data))
          return { success: true, payload: subTradeFee.data }
        })
        .catch(e => {
          return {
            success: false,
            payload: e?.response?.data?.error || e.message,
          }
        })
    } else {
      return client
        .post(`${subTradeApiPath}/add-fee`, savedSubTradeFee)
        .then(subTradeFee => {
          dispatch(dispatchAdd(subTradeFee.data))
          return { success: true, payload: subTradeFee.data }
        })
        .catch(e => {
          return {
            success: false,
            payload: e?.response?.data?.error || e.message,
          }
        })
    }
  }

export const deleteSubTradeFees =
  (subTradeFee: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    const isLocked = subTradeFee?.status?.toLowerCase() === 'settled'

    const subTradeApiPath = isLocked
      ? `${apiServerUrl}/api/locked-sub-trades`
      : `${apiServerUrl}/api/sub-trades`

    return client
      .delete(
        `${subTradeApiPath}/delete-fee?id=${subTradeFee.id}&type=${subTradeFee.feeType}`,
      )
      .then(() => {
        dispatch(deleteSubTradeFee(subTradeFee))
        return { success: true, payload: subTradeFee }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

const startAllocationHeartbeat = async (
  apiServerUrl: string,
  token: string | undefined,
  abortController: AbortController,
) => {
  try {
    const response = await fetch(
      `${apiServerUrl}/api/sub-trades/allocation-heartbeat`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,
      },
    )

    if (!response.ok) {
      console.error('SSE connection failed:', response.statusText)
      return
    }

    const reader = response.body!.getReader()
    const decoder = new TextDecoder()

    let done = false
    while (!done) {
      const result = await reader.read()
      done = result.done
      if (done) {
        console.log('Allocation heartbeat connection closed')
        break
      }

      const chunk = decoder.decode(result.value, { stream: true })

      const lines = chunk.split('\n')
      for (const line of lines) {
        if (line.startsWith('data:')) {
          const data = line.substring(5).trim()
          try {
            const parsed = JSON.parse(data)
            console.log(
              `Allocation Progress: ${parsed.stage} (${parsed.completed}/${parsed.total})`,
            )
          } catch (error) {
            console.log('Failed to parse heartbeat data:', error)
          }
        }
      }
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.log('Allocation heartbeat connection aborted')
    } else {
      console.error('SSE error:', error)
    }
  }
}

export const saveSubTradeAllocation =
  (subTradeAllocation: SubTradeAllocation) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const token = Cookies.get('access_token')
    const heartbeatController = new AbortController()

    // Start SSE heartbeat in background
    startAllocationHeartbeat(apiServerUrl, token, heartbeatController)

    try {
      return await client
        .post(
          `${apiServerUrl}/api/sub-trades/change-sub-trade-allocation`,
          subTradeAllocation,
        )
        .then(subTrade => {
          dispatch(changeAllocationSubTrade(subTrade.data))
          return { success: true, payload: subTrade.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    } finally {
      // Close SSE connection
      heartbeatController.abort()
    }
  }

export const saveWireInstructionChange =
  (data: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/sub-trades/change-wire-instruction`, data)
      .then(subTrade => {
        dispatch(changeWireInstructionSubTrade(subTrade.data))
        return { success: true, payload: subTrade.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

/**
 * `reloadLockedFMMetadatas: false` skips the tenant-wide funding-memo reload
 * below, for a caller that re-reads the funding memos itself right after
 * (the Trade detail page's Documents tab, ADR_LOS-030 step 3).
 */
type SettleOptions = { reloadLockedFMMetadatas?: boolean }

export const settleSubTrade = (data: any, options: SettleOptions = {}) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/sub-trades/settle`, data)
    .then(subTrade => {
      dispatch(settleSubTradeAction(subTrade.data))
      // Settlement auto-locks the Funding Memo on the backend (best-effort).
      // Refetch FM lock state so the lock icon and FM panels reflect the
      // actual outcome without a manual refresh. fresh: bypass any refresh
      // that started before the settle so pre-mutation data cannot win.
      if (options.reloadLockedFMMetadatas !== false) {
        dispatch(loadAllLockedFMMetadatas({ fresh: true }) as any)
      }
      dispatch(loadFMMetadataForSubTrade(data.subTradeId.id) as any)
      return { success: true, payload: subTrade.data }
    })
    .catch(e => {
      throw new Error(e.response.data.error)
    })
}

export const unsettleSubTrade = (data: any, options: SettleOptions = {}) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/locked-sub-trades/unsettle`, data)
    .then(response => {
      dispatch(unsettleSubTradeAction(response.data))
      // Unsettlement auto-unlocks the Funding Memo on the backend
      // (best-effort). Refetch FM lock state so the lock icon and FM panels
      // reflect the actual outcome without a manual refresh. fresh: bypass any
      // refresh that started before the unsettle so pre-mutation data cannot win.
      if (options.reloadLockedFMMetadatas !== false) {
        dispatch(loadAllLockedFMMetadatas({ fresh: true }) as any)
      }
      dispatch(loadFMMetadataForSubTrade(data.subTradeId.id) as any)
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const terminateSubTrade = (data: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/sub-trades/terminate`, data)
    .then(subTrade => {
      dispatch(terminateSubTradeAction(subTrade.data))
      // The backend archives the sub-trade's AA and FM SignableDocuments
      // during the Terminate flow; mirror that in Redux so the Documents
      // tab icons disappear without requiring a page refresh.
      dispatch(
        removeSignableDocumentsForContract({
          contractId: subTrade.data.id,
          documentTypes: [
            'AssignmentAgreementDocument',
            'FundingMemoDocument',
          ],
        }),
      )
      return { success: true, payload: subTrade.data }
    })
    .catch(e => {
      throw new Error(e.response.data.error)
    })
}
